import httpClient from '@/services/httpClient';

const ftpURL = process.env.VUE_APP_API_FTP_URL;

const state = {
  downloads: [],
  showOverlay: false,
};

const getters = {
  activeDownloads: state => state?.downloads?.filter(d => d.status === 'downloading') || [],
  completedDownloads: state => state?.downloads?.filter(d => d.status === 'completed') || [],
  failedDownloads: state => state?.downloads?.filter(d => d.status === 'failed') || [],
  allDownloads: state => state?.downloads || [],
};

const mutations = {
  ADD_DOWNLOAD(state, download) {
    state.downloads.unshift(download);
  },
  UPDATE_PROGRESS(state, { id, progress }) {
    const download = state.downloads.find(d => d.id === id);
    if (download) {
      download.progress = progress;
    }
  },
  UPDATE_STATUS(state, { id, status, error = null }) {
    const download = state.downloads.find(d => d.id === id);
    if (download) {
      download.status = status;
      if (error) download.error = error;
    }
  },
  REMOVE_DOWNLOAD(state, id) {
    state.downloads = state.downloads.filter(d => d.id !== id);
  },
  CLEAR_COMPLETED(state) {
    state.downloads = state.downloads.filter(
      d => d.status !== 'completed' && d.status !== 'failed'
    );
  },
  SET_SHOW_OVERLAY(state, show) {
    state.showOverlay = show;
  },
  RESET_STATE(state) {
    state.downloads = [];
    state.showOverlay = false;
  },
};

const workers = {};

const actions = {
  async startDownload({ commit }, { url, name, fileName }) {
    const id = Date.now() + Math.random().toString(36).substr(2, 9);
    const download = {
      id,
      name: fileName || name,
      progress: 0,
      status: 'downloading',
      error: null,
      fileName,
      fileExtension: '',
    };

    commit('ADD_DOWNLOAD', download);

    try {
      const urlParts = url.split('/');
      const bucketName = urlParts[2]?.split('.')[0];
      const fileKey = urlParts.slice(3).join('/');
      download.fileExtension = fileKey.split('.').pop()?.toLowerCase();

      const signedUrlRes = await httpClient.get(
        `${ftpURL}s3/files/presigned-url?bucket_name=${bucketName}&key=${fileKey}`
      );
      const signedUrl = signedUrlRes.data[0]?.url;
      if (!signedUrl) throw new Error('Failed to get signed URL');

      if (window.showSaveFilePicker) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: `${download.name}.${download.fileExtension}`,
          });
          const writable = await fileHandle.createWritable();

          const response = await fetch(signedUrl);
          if (!response.ok) throw new Error('Network response was not ok');
          const contentLength = response.headers.get('content-length');
          const total = contentLength ? parseInt(contentLength, 10) : 0;
          let loaded = 0;

          const reader = response.body.getReader();
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            await writable.write(value);
            loaded += value.length;
            if (total)
              commit('UPDATE_PROGRESS', { id, progress: Math.round((loaded / total) * 100) });
          }
          await writable.close();
          commit('UPDATE_STATUS', { id, status: 'completed' });
          return;
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('File System Access API failed, falling back to worker:', err);
          } else {
            commit('UPDATE_STATUS', { id, status: 'failed', error: 'User cancelled' });
            return;
          }
        }
      }

      const worker = new Worker('/workers/downloadWorker.js');
      workers[id] = worker;

      worker.postMessage({ type: 'START', url: signedUrl, id, skipAuth: true });

      worker.onmessage = e => {
        const { type, progress, blob, error, status } = e.data;

        if (type === 'PROGRESS') {
          commit('UPDATE_PROGRESS', { id, progress });
        } else if (type === 'STATUS') {
          commit('UPDATE_STATUS', { id, status });
        } else if (type === 'COMPLETE') {
          const downloadUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = downloadUrl;
          a.download = `${download.name}.${download.fileExtension}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(downloadUrl);

          commit('UPDATE_STATUS', { id, status: 'completed' });
          worker.terminate();
          delete workers[id];
        } else if (type === 'ERROR') {
          commit('UPDATE_STATUS', { id, status: 'failed', error });
          worker.terminate();
          delete workers[id];
        }
      };

      worker.onerror = () => {
        commit('UPDATE_STATUS', { id, status: 'failed', error: 'Worker error' });
        worker.terminate();
        delete workers[id];
      };
    } catch (error) {
      console.error('Download failed:', error);
      commit('UPDATE_STATUS', { id, status: 'failed', error: error.message });
    }
  },
  // eslint-disable-next-line no-unused-vars
  pauseDownload({ commit }, id) {
    if (workers[id]) {
      workers[id].postMessage({ type: 'PAUSE' });
    }
  },
  // eslint-disable-next-line no-unused-vars
  resumeDownload({ commit }, id) {
    if (workers[id]) {
      workers[id].postMessage({ type: 'RESUME' });
    }
  },
  cancelDownload({ commit }, id) {
    if (workers[id]) {
      workers[id].postMessage({ type: 'CANCEL' });
      setTimeout(() => {
        if (workers[id]) {
          workers[id].terminate();
          delete workers[id];
        }
      }, 100);
    }
    commit('REMOVE_DOWNLOAD', id);
  },
  removeDownload({ commit }, id) {
    if (workers[id]) {
      workers[id].terminate();
      delete workers[id];
    }
    commit('REMOVE_DOWNLOAD', id);
  },
  clearCompleted({ commit }) {
    commit('CLEAR_COMPLETED');
  },
  setShowOverlay({ commit }, show) {
    commit('SET_SHOW_OVERLAY', show);
  },
  resetModule({ commit }) {
    Object.values(workers).forEach(w => w.terminate());
    for (const id in workers) delete workers[id];
    commit('RESET_STATE');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
